/**
 * Sign Up Page — self-service enrollment with enrollment code
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';

export function SignUpPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [enrollmentCode, setEnrollmentCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await api.auth.signup({ email, password, name, enrollmentCode });
      if (result.status === 'active') {
        navigate('/login', { state: { flash: 'Account created successfully. Please sign in.' } });
      } else {
        navigate('/pending');
      }
    } catch (err: any) {
      setError(err.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" style={{ marginBottom: 12 }}>
            <circle cx="24" cy="24" r="20" fill="url(#cs-grad-su)" />
            <g stroke="#fff" strokeWidth="1.5" strokeLinecap="round" opacity="0.9">
              <line x1="24" y1="8" x2="24" y2="16" />
              <line x1="24" y1="32" x2="24" y2="40" />
              <line x1="8" y1="24" x2="16" y2="24" />
              <line x1="32" y1="24" x2="40" y2="24" />
              <line x1="12.7" y1="12.7" x2="18.3" y2="18.3" />
              <line x1="29.7" y1="29.7" x2="35.3" y2="35.3" />
              <line x1="35.3" y1="12.7" x2="29.7" y2="18.3" />
              <line x1="18.3" y1="29.7" x2="12.7" y2="35.3" />
            </g>
            <circle cx="24" cy="24" r="5" fill="#fff" />
            <defs>
              <radialGradient id="cs-grad-su" cx="0.5" cy="0.5" r="0.5">
                <stop offset="0%" stopColor="#6B95FF" />
                <stop offset="100%" stopColor="#4F7CFF" />
              </radialGradient>
            </defs>
          </svg>
          <h1 style={styles.title}>coreskan</h1>
          <p style={styles.subtitle}>Create your account</p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.field}>
            <label style={styles.label}>Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={styles.input}
              placeholder="John Doe"
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="john@company.com"
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="Create a password"
              required
              minLength={8}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Enrollment Code</label>
            <input
              type="text"
              value={enrollmentCode}
              onChange={(e) => setEnrollmentCode(e.target.value.toUpperCase())}
              style={{ ...styles.input, fontFamily: 'monospace', letterSpacing: '0.1em' }}
              placeholder="ACME-7X3K"
              required
            />
            <span style={styles.hint}>Get this from your organization administrator</span>
          </div>

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>

        <div style={styles.footer}>
          Already have an account?{' '}
          <Link to="/login" style={styles.link}>Sign in</Link>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0d0d0d 0%, #1a1a1a 100%)',
  },
  card: {
    background: '#1a1a1a',
    borderRadius: 12,
    padding: 'clamp(20px, 5vw, 40px) clamp(16px, 4vw, 36px)',
    width: 420,
    maxWidth: '90vw',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
    border: '1px solid #333333',
  },
  logo: {
    textAlign: 'center' as const,
    marginBottom: 32,
  },
  title: {
    color: '#f1f5f9',
    fontSize: 28,
    fontWeight: 600,
    margin: 0,
    letterSpacing: '0.08em',
    textTransform: 'lowercase' as const,
  },
  subtitle: {
    color: '#64748b',
    fontSize: 14,
    margin: '8px 0 0',
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 20,
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 6,
  },
  label: {
    color: '#94a3b8',
    fontSize: 13,
    fontWeight: 500,
  },
  input: {
    background: '#0d0d0d',
    border: '1px solid #333333',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#f1f5f9',
    fontSize: 16,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
    minHeight: 44,
  },
  hint: {
    color: '#475569',
    fontSize: 11,
    marginTop: 2,
  },
  button: {
    background: '#4F7CFF',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '12px 0',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
    minHeight: 44,
  },
  error: {
    background: '#7f1d1d',
    color: '#fca5a5',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
  },
  footer: {
    textAlign: 'center' as const,
    marginTop: 24,
    color: '#64748b',
    fontSize: 13,
  },
  link: {
    color: '#4F7CFF',
    textDecoration: 'none',
  },
};
